/**
 * Combined Cart Upsell Slider & Optimized Variant Switcher
 * File: assets/cart-upsell-list.js
 * Fixed to work after product toggle AND cart icon clicks
 * FIX (this version): on load, force each upsell card to show ITS OWN
 * default/selected variant image instead of whatever image happened to be
 * in the markup — which was bleeding through as the main PDP's variant
 * image when the upsell product matched the product being viewed.
 */
(function() {
  'use strict';
  
  if (window.CartUpsellCombinedLoaded) return;
  window.CartUpsellCombinedLoaded = true;

  let sliderInstances = new Map();

  // SLIDER FUNCTIONALITY (optimized)
  function initSlider(carousel) {
    if (sliderInstances.has(carousel)) {
      sliderInstances.get(carousel).destroy();
      sliderInstances.delete(carousel);
    }

    const wrapper = carousel.closest('.cart-upsell-carousel-wrapper');
    const [prevBtn, nextBtn] = [wrapper?.querySelector('.prev-next-button--prev'), wrapper?.querySelector('.prev-next-button--next')];
    
    if (!prevBtn || !nextBtn) return;
    
    const slides = carousel.querySelectorAll('.upsell_product');
    if (slides.length <= 1) {
      prevBtn.style.display = nextBtn.style.display = 'none';
      return;
    }

    let currentIndex = 0;

    // FIX: maxIndex is now computed live instead of cached once at init time.
    // Caching it once caused stale/wrong values if the cart drawer was still
    // animating open (offsetWidth 0 or mid-transition) when initSlider ran,
    // which made the next/prev arrows look clickable but silently do nothing.
    const getMaxIndex = () => {
      const currentSlideWidth = slides[0].offsetWidth + 10;
      if (!currentSlideWidth) return 0;
      return Math.max(0, slides.length - Math.floor(carousel.offsetWidth / currentSlideWidth));
    };

    // NEW: flag + timer for manual-swipe snapping
    let isProgrammaticScroll = false;
    let scrollEndTimer = null;

    // FIX: shared "which slide is closest to current scroll position" logic,
    // used both on scroll and to sync currentIndex right after init. This
    // matters because the carousel element (and its scrollLeft) usually
    // survives closing/reopening the cart drawer — only initSlider() re-runs.
    // Without this, currentIndex always restarted at 0 regardless of where
    // the carousel was actually scrolled to, causing next/prev to move to
    // the wrong slide or refuse to move at all after reopening the drawer.
    const getClosestIndex = () => {
      let closest = 0, minDiff = Infinity;
      slides.forEach((s, i) => {
        const diff = Math.abs(s.offsetLeft - carousel.scrollLeft);
        if (diff < minDiff) { minDiff = diff; closest = i; }
      });
      return closest;
    };
    
    const updateButtons = () => {
      const scrollLeft = carousel.scrollLeft;
      const maxScroll = carousel.scrollWidth - carousel.clientWidth;
      
      prevBtn.disabled = scrollLeft <= 0;
      nextBtn.disabled = scrollLeft >= maxScroll - 1;
      prevBtn.style.display = nextBtn.style.display = '';
    };

    const scrollToSlide = (index) => {
      isProgrammaticScroll = true; // NEW
      carousel.scrollTo({ left: slides[index].offsetLeft, behavior: 'smooth' });
      currentIndex = index;
      setTimeout(() => {
        isProgrammaticScroll = false; // NEW
        updateButtons();
      }, 300);
    };

    const handlePrevClick = (e) => {
      e.preventDefault();
      if (currentIndex > 0) scrollToSlide(currentIndex - 1);
    };

    const handleNextClick = (e) => {
      e.preventDefault();
      if (currentIndex < getMaxIndex()) scrollToSlide(currentIndex + 1); // FIX: live maxIndex
    };

    const handleScroll = () => {
      currentIndex = Math.max(0, Math.min(getClosestIndex(), getMaxIndex())); // FIX: live maxIndex
      updateButtons();

      // NEW: don't snap while a programmatic (button) scroll is animating
      if (isProgrammaticScroll) return;

      // NEW: wait until the user's swipe momentum settles, then snap
      clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(() => {
        scrollToSlide(currentIndex);
      }, 120);
    };

    prevBtn.addEventListener('click', handlePrevClick);
    nextBtn.addEventListener('click', handleNextClick);
    carousel.addEventListener('scroll', handleScroll);

    sliderInstances.set(carousel, {
      destroy() {
        prevBtn.removeEventListener('click', handlePrevClick);
        nextBtn.removeEventListener('click', handleNextClick);
        carousel.removeEventListener('scroll', handleScroll);
        clearTimeout(scrollEndTimer); // NEW: avoid leaking a snap after teardown
      }
    });

    // FIX: sync currentIndex to wherever the carousel is actually scrolled
    // to right now, instead of assuming it starts at slide 0. This is the
    // key fix for next/prev misbehaving after closing and reopening the
    // cart drawer (see getClosestIndex comment above for why).
    currentIndex = Math.max(0, Math.min(getClosestIndex(), getMaxIndex()));

    updateButtons();
  }

  // VARIANT SWITCHER (optimized)

  // FIX: pulled the "apply a variant to a card" logic out of the change-event
  // handler so it can be reused both on swatch click AND on page load.
  function applyVariantToCard(card, variantId) {
    if (!card || !variantId) return;

    const productId = card.dataset.productId;
    const variantInput = card.querySelector('[data-upsell-variant-id]');
    const mediaCarousel = card.querySelector('[data-media-carousel]');

    if (!variantInput || !mediaCarousel || !productId) return;

    const variantMediaMap = getVariantMediaMap(productId);
    const variantData = variantMediaMap?.[variantId];

    if (!variantData) return;

    variantInput.value = variantId;
    variantInput.disabled = variantData.available === false;

    if (variantData.src) {
      switchActiveImage(mediaCarousel, variantData.mediaId, variantData.src, variantData.srcset);
    }
  }

  function handleVariantChange(event) {
    const swatch = event.target;
    const card = swatch.closest('.cart-recommendations-container [data-upsell-card]');
    if (!card) return;

    applyVariantToCard(card, swatch.dataset.variantId);
  }

  // NEW: on load, each upsell card must show ITS OWN default/selected
  // variant image — never the main PDP's currently viewed variant, even
  // if the upsell card happens to be for the same product. This runs once
  // per card and re-derives the correct variantId from the card itself.
  function getCardsOwnSelectedVariantId(card) {
    // Try the most common ways a card marks "this swatch is the active one".
    // ADJUST THESE SELECTORS if your swatch markup differs.
    const selectedSwatch = card.querySelector(
      '.product-card-color-swatch:checked, ' +
      '.product-card-color-swatch[data-selected="true"], ' +
      '.product-card-color-swatch.is-selected, ' +
      '.product-card-color-swatch[aria-checked="true"]'
    );

    if (selectedSwatch?.dataset.variantId) {
      return selectedSwatch.dataset.variantId;
    }

    // Fall back to the first swatch rendered on the card, if any.
    const firstSwatch = card.querySelector('.product-card-color-swatch');
    if (firstSwatch?.dataset.variantId) {
      return firstSwatch.dataset.variantId;
    }

    // Last resort: whatever is already in the hidden variant input.
    const variantInput = card.querySelector('[data-upsell-variant-id]');
    return variantInput?.value || null;
  }

  function initUpsellVariantImages() {
    const cards = document.querySelectorAll('.cart-recommendations-container [data-upsell-card]');
    cards.forEach((card) => {
      const variantId = getCardsOwnSelectedVariantId(card);
      if (variantId) applyVariantToCard(card, variantId);
    });
  }

  function getVariantMediaMap(productId) {
    const scriptTag = document.querySelector(`[data-variant-media-map-${productId}]`);
    if (!scriptTag) return null;
    
    try {
      return JSON.parse(scriptTag.textContent);
    } catch (e) {
      console.warn('Failed to parse variant media map:', e);
      return null;
    }
  }
  
  function switchActiveImage(mediaCarousel, newMediaId, newSrc, newSrcset) {
    if (!mediaCarousel) return;
    const img = mediaCarousel.querySelector('.upsell-card__image--active');
    if (!img) return;
    if (newSrc) img.src = newSrc;
    if (newSrcset) img.srcset = newSrcset;
    if (newMediaId) img.dataset.mediaId = newMediaId;
  }

  // INITIALIZATION
  function init() {
    const carousels = document.querySelectorAll('.cart-drawer__recommendations scroll-carousel#carousel');
    carousels.forEach(carousel => setTimeout(() => initSlider(carousel), 50));

    // NEW: correct each upsell card's image right after (re)render.
    setTimeout(initUpsellVariantImages, 50);
    
    if (!window.variantSwitcherInitialized) {
      document.addEventListener('change', function(event) {
        if (event.target.matches('.product-card-color-swatch')) {
          handleVariantChange(event);
        }
      });
      window.variantSwitcherInitialized = true;
    }
  }

  function cleanup() {
    sliderInstances.forEach(instance => instance.destroy());
    sliderInstances.clear();
  }

  let initTimeout;
  const debouncedInit = () => {
    clearTimeout(initTimeout);
    initTimeout = setTimeout(() => {
      cleanup();
      init();
    }, 150);
  };

  // FIX: Check if cart drawer is visible and reinitialize
  const checkCartDrawerAndInit = () => {
    const cartDrawer = document.querySelector('#cart-drawer, .cart-drawer, [data-cart-drawer]');
    if (cartDrawer) {
      const isVisible = cartDrawer.classList.contains('active') || 
                       cartDrawer.classList.contains('open') || 
                       cartDrawer.style.display !== 'none' ||
                       getComputedStyle(cartDrawer).display !== 'none';
      
      if (isVisible) {
        // Wait for content to load then initialize
        setTimeout(debouncedInit, 200);
      }
    }
  };

  // Initialize when ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Handle cart updates
  document.addEventListener('cart:updated', debouncedInit);
  document.addEventListener('cart:refresh', debouncedInit);
  document.addEventListener('scripts:reloaded', debouncedInit);

  // FIX: Listen for cart drawer opens (cart icon clicks)
  document.addEventListener('click', function(event) {
    // Check if clicked element or its parent is a cart trigger
    const cartTrigger = event.target.closest('[href="/cart"], [data-cart-trigger], .cart-link, .header__icon--cart, [onclick*="cart"]');
    if (cartTrigger) {
      setTimeout(checkCartDrawerAndInit, 100);
    }
  });

  // FIX: Listen for drawer state changes
  const cartStateObserver = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'attributes' && 
          (mutation.attributeName === 'class' || mutation.attributeName === 'style')) {
        const target = mutation.target;
        if (target.matches('#cart-drawer, .cart-drawer, [data-cart-drawer]')) {
          checkCartDrawerAndInit();
        }
      }
    });
  });

  // Observe cart drawer for visibility changes
  const cartDrawer = document.querySelector('#cart-drawer, .cart-drawer, [data-cart-drawer]');
  if (cartDrawer) {
    cartStateObserver.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['class', 'style']
    });

    // Also observe for content changes
    const contentObserver = new MutationObserver(function(mutations) {
      const shouldReinit = mutations.some(mutation => 
        mutation.type === 'childList' && 
        Array.from(mutation.addedNodes).some(node => 
          node.nodeType === 1 && 
          (node.classList?.contains('cart-recommendations-container') ||
           node.querySelector?.('scroll-carousel#carousel'))
        )
      );
      
      if (shouldReinit) debouncedInit();
    });

    contentObserver.observe(cartDrawer, {
      childList: true,
      subtree: true
    });
  }

  window.cleanupUpsellSliders = cleanup;
})();